import React, { useState, useMemo, useCallback } from 'react';
import { useData } from '../../../contexts/DataContext';
import './RecurrenceGenerator.css';

const WEEKDAYS = [
  { key: 'MO', label: 'Mon', dayIndex: 1 },
  { key: 'TU', label: 'Tue', dayIndex: 2 },
  { key: 'WE', label: 'Wed', dayIndex: 3 },
  { key: 'TH', label: 'Thu', dayIndex: 4 },
  { key: 'FR', label: 'Fri', dayIndex: 5 },
  { key: 'SA', label: 'Sat', dayIndex: 6 },
  { key: 'SU', label: 'Sun', dayIndex: 0 },
];

/**
 * RecurrenceGenerator — collapsible panel that lets users define a
 * weekly recurrence pattern (days-of-week + time + date range) and
 * generate individual draft class-session events in bulk.
 */
export default function RecurrenceGenerator({ courseData, setCourseData }) {
  const { addDraftEvent, createCourseDraft, setCreateCourseDraft } = useData();

  const [expanded, setExpanded] = useState(false);
  const [selectedDays, setSelectedDays] = useState(new Set());
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [startDate, setStartDate] = useState(courseData?.startDate || '');
  const [endDate, setEndDate] = useState(courseData?.endDate || '');
  const [location, setLocation] = useState(courseData?.location || '');
  const [titleTemplate, setTitleTemplate] = useState('');
  const [generating, setGenerating] = useState(false);
  const [lastGenCount, setLastGenCount] = useState(null);

  // Sync date range from courseData when set
  React.useEffect(() => {
    if (courseData?.startDate && !startDate) setStartDate(courseData.startDate);
    if (courseData?.endDate && !endDate) setEndDate(courseData.endDate);
  }, [courseData?.startDate, courseData?.endDate, startDate, endDate]);

  const toggleDay = useCallback((key) => {
    setSelectedDays(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Compute matching dates
  const generatedDates = useMemo(() => {
    if (selectedDays.size === 0 || !startDate || !endDate) return [];
    const dayIndices = new Set(
      WEEKDAYS.filter(w => selectedDays.has(w.key)).map(w => w.dayIndex)
    );
    const dates = [];
    const [sy, sm, sd] = startDate.split('-').map(Number);
    const [ey, em, ed] = endDate.split('-').map(Number);
    const cursor = new Date(sy, sm - 1, sd);
    const end = new Date(ey, em - 1, ed);
    while (cursor <= end) {
      if (dayIndices.has(cursor.getDay())) {
        dates.push(new Date(cursor));
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return dates;
  }, [selectedDays, startDate, endDate]);

  const sessionCount = generatedDates.length;

  const handleGenerate = useCallback(async () => {
    if (sessionCount === 0) return;
    setGenerating(true);

    const existingClassSessions = (createCourseDraft?.draftEvents || [])
      .filter(e => e.eventType === 'class_session' && e.isRecurring);
    const hasExisting = existingClassSessions.length > 0;

    if (hasExisting) {
      const action = window.confirm(
        `You already have ${existingClassSessions.length} recurring class session(s). Press OK to replace them, or Cancel to append the new sessions.`
      );
      if (action) {
        // Remove existing recurring class sessions
        setCreateCourseDraft(prev => ({
          ...prev,
          draftEvents: (prev.draftEvents || []).filter(
            e => !(e.eventType === 'class_session' && e.isRecurring)
          ),
        }));
      }
    }

    const baseName = titleTemplate.trim() || courseData?.courseName || 'Class';

    for (let i = 0; i < generatedDates.length; i++) {
      const d = generatedDates[i];
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      await addDraftEvent({
        title: `${baseName} – Session ${i + 1}`,
        start: `${dateStr}T${startTime || '00:00'}:00`,
        end: `${dateStr}T${endTime || startTime || '00:00'}:00`,
        location: location.trim(),
        description: '',
        eventType: 'class_session',
        isRecurring: true,
        sequence: i + 1,
        isFromSyllabus: false,
      });
    }

    // Also update courseData start/end dates if not set
    if (!courseData?.startDate || !courseData?.endDate) {
      setCourseData(prev => ({
        ...prev,
        startDate: prev.startDate || startDate,
        endDate: prev.endDate || endDate,
      }));
    }

    // Build an RRULE string for metadata
    const byday = Array.from(selectedDays).join(',');
    const rrule = `FREQ=WEEKLY;BYDAY=${byday};WKST=SU`;
    setCourseData(prev => ({
      ...prev,
      schedulePattern: rrule,
      meetingStartTime: startTime,
      meetingEndTime: endTime,
    }));

    setLastGenCount(generatedDates.length);
    setGenerating(false);
  }, [
    sessionCount, createCourseDraft, setCreateCourseDraft,
    generatedDates, startTime, endTime, location, titleTemplate,
    courseData, setCourseData, addDraftEvent, selectedDays, startDate, endDate,
  ]);

  const isValid = selectedDays.size > 0 && startDate && endDate && sessionCount > 0;

  if (!expanded) {
    return (
      <div className="recurrence-gen-collapsed">
        <button
          type="button"
          className="recurrence-gen-trigger"
          onClick={() => setExpanded(true)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4" /><path d="M8 2v4" /><path d="M3 10h18" />
            <path d="M12 14v4" /><path d="M10 16h4" />
          </svg>
          Generate Recurring Classes
        </button>
        {lastGenCount != null && (
          <span className="recurrence-gen-last">✓ {lastGenCount} sessions generated</span>
        )}
      </div>
    );
  }

  return (
    <div className="recurrence-gen">
      <div className="recurrence-gen-header">
        <h4 className="recurrence-gen-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <path d="M16 2v4" /><path d="M8 2v4" /><path d="M3 10h18" />
            <path d="M12 14v4" /><path d="M10 16h4" />
          </svg>
          Generate Recurring Classes
        </h4>
        <button type="button" className="recurrence-gen-close" onClick={() => setExpanded(false)} aria-label="Collapse">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 15l-6-6-6 6" /></svg>
        </button>
      </div>

      <div className="recurrence-gen-body">
        {/* Days of Week */}
        <div className="rg-field">
          <label className="rg-label">Days of Week <span className="required">*</span></label>
          <div className="rg-day-pills">
            {WEEKDAYS.map(w => (
              <button
                key={w.key}
                type="button"
                className={`rg-day-pill ${selectedDays.has(w.key) ? 'active' : ''}`}
                onClick={() => toggleDay(w.key)}
                aria-pressed={selectedDays.has(w.key)}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>

        {/* Time */}
        <div className="rg-row">
          <div className="rg-field">
            <label className="rg-label" htmlFor="rg-startTime">Start Time</label>
            <input id="rg-startTime" type="time" className="rg-input" value={startTime} onChange={e => setStartTime(e.target.value)} />
          </div>
          <div className="rg-field">
            <label className="rg-label" htmlFor="rg-endTime">End Time</label>
            <input id="rg-endTime" type="time" className="rg-input" value={endTime} onChange={e => setEndTime(e.target.value)} />
          </div>
        </div>

        {/* Date Range */}
        <div className="rg-row">
          <div className="rg-field">
            <label className="rg-label" htmlFor="rg-startDate">Start Date <span className="required">*</span></label>
            <input id="rg-startDate" type="date" className="rg-input" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div className="rg-field">
            <label className="rg-label" htmlFor="rg-endDate">End Date <span className="required">*</span></label>
            <input id="rg-endDate" type="date" className="rg-input" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
        </div>

        {/* Location */}
        <div className="rg-field">
          <label className="rg-label" htmlFor="rg-location">Location (optional)</label>
          <input id="rg-location" type="text" className="rg-input" placeholder="e.g. Room 204, Science Hall" value={location} onChange={e => setLocation(e.target.value)} />
        </div>

        {/* Title Template */}
        <div className="rg-field">
          <label className="rg-label" htmlFor="rg-title">Session Title (optional)</label>
          <input id="rg-title" type="text" className="rg-input" placeholder={`${courseData?.courseName || 'Class'} – Session 1, 2, 3…`} value={titleTemplate} onChange={e => setTitleTemplate(e.target.value)} />
        </div>

        {/* Preview & Generate */}
        <div className="rg-footer">
          {isValid ? (
            <span className="rg-preview">
              This will create <strong>{sessionCount}</strong> class session{sessionCount !== 1 ? 's' : ''}
              {startDate && endDate && (
                <> from {new Date(startDate + 'T00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} to {new Date(endDate + 'T00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</>
              )}
            </span>
          ) : (
            <span className="rg-preview rg-preview-hint">
              {selectedDays.size === 0 ? 'Select at least one day of the week' : !startDate || !endDate ? 'Set start and end dates' : 'Configure your recurrence pattern'}
            </span>
          )}
          <button
            type="button"
            className="rg-generate-btn"
            disabled={!isValid || generating}
            onClick={handleGenerate}
          >
            {generating ? (
              <><span className="inline-spinner" aria-hidden="true" /> Generating…</>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14"/><path d="M5 12h14"/></svg>
                Generate {sessionCount > 0 ? `${sessionCount} Sessions` : 'Sessions'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
